import React, { useState, useCallback, useEffect } from 'react'
import { LogIn, Eye, EyeOff, Building2, MapPin, ArrowRight } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore.js'
import { trpc } from '../../api/index.js'

type PunctInfo = {
  id: string
  storeDbId: string
  codul: string
  denumire: string
  adresa: string | null
  oras: string | null
}

function AppLogo() {
  const aspectQ = trpc.setari.getAspect.useQuery(undefined, { staleTime: Infinity, retry: false })
  const logo = aspectQ.data?.logoBase64

  return (
    <div className="text-center">
      <div className="w-16 h-16 rounded-2xl bg-brand-600/20 border border-brand-600/30 flex items-center justify-center mx-auto mb-4 overflow-hidden">
        {logo
          ? <img src={logo} alt="Logo" className="w-full h-full object-contain p-1" />
          : <img src="/favicon.svg" alt="rentCORE" className="w-10 h-10" />
        }
      </div>
      <h1 className="text-2xl font-bold text-white tracking-tight">
        rent<span className="text-brand-400">CORE</span>
      </h1>
      <p className="text-xs text-slate-500 mt-1.5 max-w-[240px] mx-auto leading-relaxed">
        Gestiune închirieri · Echipamente și utilaje pentru proiectele tale de construcții
      </p>
    </div>
  )
}

function PunctSelectStep({
  puncte,
  onSelect,
  isPending,
}: {
  puncte: PunctInfo[]
  onSelect: (p: PunctInfo) => void
  isPending: boolean
}) {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <p className="text-sm font-medium text-white mb-1">Selectați punctul de lucru</p>
        <p className="text-xs text-slate-500">Contul tău are acces la mai multe locații</p>
      </div>
      <div className="space-y-2">
        {puncte.map(p => (
          <button
            key={p.id}
            onClick={() => onSelect(p)}
            disabled={isPending}
            className="w-full flex items-center gap-3 px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-brand-500/50 rounded-xl text-left transition-colors disabled:opacity-50 group"
          >
            <div className="w-8 h-8 rounded-lg bg-brand-600/20 border border-brand-600/30 flex items-center justify-center shrink-0">
              <Building2 size={15} className="text-brand-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-brand-400">{p.codul}</span>
                <span className="text-sm font-medium text-white truncate">{p.denumire}</span>
              </div>
              {(p.adresa || p.oras) && (
                <div className="flex items-center gap-1 mt-0.5">
                  <MapPin size={10} className="text-slate-500 shrink-0" />
                  <span className="text-xs text-slate-500 truncate">
                    {[p.adresa, p.oras].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
            <ArrowRight size={14} className="text-slate-600 group-hover:text-brand-400 transition-colors shrink-0" />
          </button>
        ))}
      </div>
    </div>
  )
}

export function LoginScreen() {
  const { setSession } = useAuthStore()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  // Pasul 2: selecție punct de lucru
  const [step, setStep] = useState<'credentials' | 'punct'>('credentials')
  const [pendingToken, setPendingToken] = useState('')
  const [pendingUser, setPendingUser] = useState<any>(null)
  const [availablePuncte, setAvailablePuncte] = useState<PunctInfo[]>([])

  /**
   * Tokenul provizoriu de la pasul 2 are 8h de valabilitate. Dacă utilizatorul abandona
   * selecția punctului de lucru (închidea tabul, se întorcea la credențiale, pleca de la
   * ghișeu), tokenul rămânea în `localStorage`-ul unui dispozitiv posibil partajat — în
   * timp ce el credea că nu s-a autentificat niciodată, pentru că a revăzut ecranul de
   * login. Îl ștergem la orice ieșire din pasul 2 fără sesiune stabilită.
   */
  useEffect(() => {
    if (step !== 'punct') return
    const curata = () => { if (!useAuthStore.getState().session) localStorage.removeItem('token') }
    window.addEventListener('beforeunload', curata)
    return () => {
      window.removeEventListener('beforeunload', curata)
      curata()
    }
  }, [step])

  const switchMut = trpc.auth.switchPunctLucru.useMutation({
    onSuccess: (data) => {
      setSession({
        userId:           pendingUser.id,
        username:         pendingUser.username,
        rol:              pendingUser.rol,
        numeComplet:      pendingUser.numeComplet ?? pendingUser.username,
        punctLucruId:     data.punctLucruId,
        punctCodul:       data.codul ?? '',
        punctDenumire:    data.denumire ?? '',
        storeDbId:        data.storeDbId,
        puncteLucruAcces: pendingUser.puncteLucruAcces ?? null,
        token:            data.token,
      })
    },
    onError: (e) => setError(e.message ?? 'Eroare la schimbarea punctului de lucru.'),
  })

  const loginMut = trpc.auth.login.useMutation({
    onSuccess: (data) => {
      // Pasul 2 dacă sunt disponibile mai multe puncte
      if (data.availablePuncte && data.availablePuncte.length > 1) {
        setPendingToken(data.token)
        setPendingUser(data.user)
        setAvailablePuncte(data.availablePuncte as PunctInfo[])
        // Token provizoriu, necesar pentru `switchPunctLucru` (apel autentificat).
        // Curățat automat dacă pasul 2 e abandonat — vezi efectul de mai jos.
        localStorage.setItem('token', data.token)
        setStep('punct')
        return
      }
      // Un singur punct — intră direct
      const pl = data.availablePuncte?.[0]
      setSession({
        userId:           data.user.id,
        username:         data.user.username,
        rol:              data.user.rol,
        numeComplet:      data.user.numeComplet ?? data.user.username,
        punctLucruId:     data.user.punctLucruId,
        punctCodul:       pl?.codul ?? '',
        punctDenumire:    pl?.denumire ?? '',
        storeDbId:        data.user.storeDbId,
        puncteLucruAcces: data.user.puncteLucruAcces ?? null,
        token:            data.token,
      })
    },
    onError: (e) => {
      setError(e.message ?? 'Utilizator sau parolă incorecte.')
      setPassword('')
    },
  })

  const handleLogin = useCallback(() => {
    if (!username.trim()) { setError('Introdu numele de utilizator.'); return }
    if (!password) { setError('Introdu parola.'); return }
    setError('')
    loginMut.mutate({ username: username.trim(), password })
  }, [username, password, loginMut])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin()
  }, [handleLogin])

  const handleSelectPunct = (p: PunctInfo) => {
    switchMut.mutate(p.id)
  }

  return (
    <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50">
      <div className="w-80 space-y-6">
        <AppLogo />

        {step === 'punct' ? (
          <PunctSelectStep
            puncte={availablePuncte}
            onSelect={handleSelectPunct}
            isPending={switchMut.isPending}
          />
        ) : (
          <div onKeyDown={handleKeyDown}>
            <div className="space-y-4">
              <div>
                <label htmlFor="login-username" className="block text-xs font-medium text-slate-400 mb-1">Utilizator</label>
                <input
                  id="login-username"
                  type="text"
                  value={username}
                  onChange={e => { setUsername(e.target.value); setError('') }}
                  placeholder="ex: admin"
                  autoFocus
                  autoComplete="username"
                  className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
                />
              </div>

              <div>
                <label htmlFor="login-password" className="block text-xs font-medium text-slate-400 mb-1">Parolă</label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError('') }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full px-3 py-2.5 pr-10 bg-slate-800 border border-slate-700 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleLogin}
                disabled={loginMut.isPending || !password || !username}
                className="w-full h-12 rounded-xl bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium transition-colors flex items-center justify-center gap-2 active:scale-95"
              >
                {loginMut.isPending ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn size={18} />
                    Intră în cont
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-700/40 rounded-xl px-4 py-3 text-sm text-red-300 text-center">
            {error}
          </div>
        )}

        {switchMut.isPending && (
          <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <div className="w-3 h-3 border border-slate-600 border-t-brand-400 rounded-full animate-spin" />
            Se conectează la punct de lucru...
          </div>
        )}
      </div>
    </div>
  )
}
